import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactsModal } from '../ContactsModal';
import { PhotosGalleryProps } from '../../PhotosGallery';
import {
  AllPhotoButton,
  ContactButton,
  ContactButtonText,
  ContactsContainer,
  ContactsInfoContainer,
  ContactsTitle
} from './styled';
import useScreenSize from '../../../../hooks/useScreenSize';
import { useClient } from '../../../../hooks/useClient';
import { FlexBetween } from '../../../../styled/common';
import { Typography } from '../../../ui/Typography';
import { ContactsButtonEnum } from './types';
import { PhoneButtonTargetTypeEnum } from '../../../../hooks/useFullPhoneNumbers';

const FIRST_PHOTO = 1;

interface ContactsSectionProps extends PhotosGalleryProps {
  onOpenGallery: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    photoNumber?: number
  ) => void;
}

export const ContactsSection: React.FC<ContactsSectionProps> = memo(
  ({ residence, onClickHandler, phonesShown, onOpenGallery }) => {
    const { t } = useTranslation();
    const isClient = useClient();

    const { isSmallOrLargeDesktop } = useScreenSize();
    const [showContacts, setShowContacts] = useState(false);

    const clickHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      onClickHandler(0, true)(e, PhoneButtonTargetTypeEnum.contact);
      setShowContacts(true);
    };

    const scrollHandler = (selector: string) => {
      const element = document.getElementById(selector);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    };

    const shownOnDesk = !isClient || isSmallOrLargeDesktop;

    const contactButtons = useMemo(
      () => [
        {
          key: ContactsButtonEnum.contacts,
          text: t('residences.numbers'),
          stat: 'residence-contacts',
          onClick: clickHandler,
          isShown: true
        },
        {
          key: ContactsButtonEnum.layouts,
          text: t('residences.apartments'),
          stat: 'residence-layouts',
          onClick: () => scrollHandler('ap-filter'),
          isShown: !!residence.apartments?.length
        },
        {
          key: ContactsButtonEnum.items,
          text: t(shownOnDesk ? 'search.form.show_ads' : 'residences.items'),
          stat: 'residence-flats',
          onClick: () => scrollHandler('residence-items'),
          isShown: !!residence.itemsCount
        }
      ],
      [residence.apartments, residence.itemsCount, shownOnDesk, t]
    );

    return (
      <>
        <ContactsContainer>
          <ContactsTitle tag="h1" data-cy="contacts__title">
            {residence.name}
          </ContactsTitle>
          <FlexBetween>
            <ContactsInfoContainer>
              {contactButtons.map(button => {
                if (!button.isShown) return null;

                return (
                  <ContactButton
                    key={button.key}
                    data-stat={button.stat}
                    $variant={button.key}
                    onClick={button.onClick}
                    role="button"
                  >
                    <ContactButtonText tag="body_1">
                      {button.text}
                    </ContactButtonText>
                  </ContactButton>
                );
              })}
            </ContactsInfoContainer>
            <AllPhotoButton onClick={e => onOpenGallery(e, FIRST_PHOTO)}>
              <Typography tag="caption_1_new">
                {t('pages.current_item.buttons.all_photos')}
              </Typography>
            </AllPhotoButton>
          </FlexBetween>
        </ContactsContainer>
        {showContacts && (
          <ContactsModal
            phonesShown={phonesShown || residence.phonesMasked || []}
            isOpen={showContacts}
            onClose={() => setShowContacts(false)}
            contactEmail={residence.contactEmail}
            website={residence.website}
          />
        )}
      </>
    );
  }
);

ContactsSection.displayName = `ContactsSection`;
